import { useCallback, useRef, useEffect, useMemo } from 'react';
import "../../../_proto/landscope-map-viewer_pb";
import { useUserProjectsContext } from '../../contexts/UserProjectsContext';
import { useAuth } from '../../contexts/AuthContext';
import { initialArtifactState, } from './types';
import { useArtifactState } from './useArtifactState';
import { createLoadArtifact } from './loaders';
import { createToggleArtifact, createHideAllArtifacts, createUpdateArtifactOpacity, createUpdateArtifactWeight, createUpdateArtifactMonochrome, createIsArtifactVisible, createIsArtifactLoading, createValidateArtifactState, } from './useArtifactVisibility';
import { createUnloadArtifactFromMemory, createReloadArtifact, createClearAll, } from './useArtifactMemory';
export const useArtifacts = (viewer, selectedProject, addStatus) => {
    const viewerRef = useRef(viewer);
    const { isAuthenticated } = useAuth();
    const { projects: userProjects } = useUserProjectsContext();
    const isDemoProject = useMemo(() => {
        var _a;
        if (!((_a = selectedProject === null || selectedProject === void 0 ? void 0 : selectedProject.getId) === null || _a === void 0 ? void 0 : _a.call(selectedProject)))
            return false;
        const projectId = selectedProject.getId();
        return !userProjects.some(userProject => userProject.id === projectId);
    }, [selectedProject, userProjects]);
    useEffect(() => {
        viewerRef.current = viewer;
    }, [viewer]);
    const currentProjectIdRef = useRef(null);
    const { artifactState, artifactActions, setArtifactState } = useArtifactState();
    useEffect(() => {
        var _a;
        const newProjectId = ((_a = selectedProject === null || selectedProject === void 0 ? void 0 : selectedProject.getId) === null || _a === void 0 ? void 0 : _a.call(selectedProject)) || (selectedProject === null || selectedProject === void 0 ? void 0 : selectedProject.id) || null;
        const currentProjectId = currentProjectIdRef.current;
        if (currentProjectId !== null && newProjectId !== currentProjectId) {
            const currentViewer = viewerRef.current;
            if (currentViewer) {
                artifactState.loaded.forEach((artifact) => {
                    try {
                        if (artifact.type === 'raster') {
                            currentViewer.imageryLayers.remove(artifact.cesiumObject);
                        }
                        else if (artifact.type === 'vector') {
                            currentViewer.dataSources.remove(artifact.cesiumObject);
                        }
                        else if (artifact.type === 'placemark') {
                            currentViewer.entities.remove(artifact.cesiumObject);
                        }
                        if (artifact.blobUrl) {
                            URL.revokeObjectURL(artifact.blobUrl);
                        }
                    }
                    catch (error) {
                        console.warn('Error clearing artifact during project switch:', error);
                    }
                });
                currentViewer.entities.removeById('user-project-polygon');
                currentViewer.entities.removeById('user-project-label');
            }
            setArtifactState(initialArtifactState);
            const clearedCount = artifactState.loaded.size;
            addStatus(`Cleared ${clearedCount} artifact${clearedCount !== 1 ? 's' : ''} from previous project (${currentProjectId}) - memory optimized`);
        }
        currentProjectIdRef.current = newProjectId;
    }, [selectedProject, addStatus]);
    // Blob URLs outlive the Cesium viewer: destroying the viewer drops the
    // imagery layers and data sources, but the underlying PNG/KML bytes stay
    // pinned to the document until each URL is revoked. Mirror the loaded map
    // into a ref so the unmount-only cleanup can reach the final state.
    const loadedRef = useRef(artifactState.loaded);
    useEffect(() => {
        loadedRef.current = artifactState.loaded;
    }, [artifactState.loaded]);
    useEffect(() => {
        return () => {
            loadedRef.current.forEach((artifact) => {
                if (artifact.blobUrl) {
                    URL.revokeObjectURL(artifact.blobUrl);
                }
            });
        };
    }, []);
    useEffect(() => {
        if (artifactState.activeOverlays.screen.length > 0) {
            setArtifactState(prev => {
                const needsUpdate = prev.activeOverlays.screen.some((overlay, index) => {
                    const expectedY = 20 + (index * 180);
                    return overlay.position.y !== expectedY;
                });
                if (needsUpdate) {
                    return {
                        ...prev,
                        activeOverlays: {
                            ...prev.activeOverlays,
                            screen: prev.activeOverlays.screen.map((overlay, index) => ({
                                ...overlay,
                                position: { x: 20, y: 20 + (index * 180) }
                            }))
                        }
                    };
                }
                return prev;
            });
        }
    }, [artifactState.activeOverlays.screen.length]);
    const loaderDeps = useMemo(() => ({
        viewer: viewerRef.current,
        artifactActions,
        artifactState,
        addStatus
    }), [artifactActions, artifactState, addStatus]);
    const loadArtifact = useCallback(createLoadArtifact(loaderDeps, {
        viewer: viewerRef.current,
        viewerRef,
        isDemoProject,
    }), [loaderDeps, isDemoProject]);
    const unloadArtifactFromMemory = useCallback(createUnloadArtifactFromMemory({
        viewerRef,
        artifactActions,
        addStatus
    }), [artifactActions, addStatus]);
    const reloadArtifact = useCallback((artifactId, shouldSetVisible = true) => {
        return createReloadArtifact({
            viewerRef,
            artifactState,
            artifactActions,
            addStatus,
            loadArtifact
        })(artifactId, shouldSetVisible);
    }, [artifactState, artifactActions, addStatus, loadArtifact]);
    const visibilityDeps = useMemo(() => ({
        viewerRef,
        artifactState,
        artifactActions,
        addStatus,
        reloadArtifact,
        unloadArtifactFromMemory
    }), [artifactState, artifactActions, addStatus, reloadArtifact, unloadArtifactFromMemory]);
    const toggleArtifact = useCallback((artifactId, visible) => createToggleArtifact(visibilityDeps)(artifactId, visible), [visibilityDeps]);
    const hideAllArtifacts = useCallback(() => createHideAllArtifacts(visibilityDeps)(), [visibilityDeps]);
    const restoreLayers = useCallback((configs) => {
        const configArtifactIds = new Set(configs.map(c => c.artifactId));
        // Hide artifacts NOT in config
        artifactState.loaded.forEach((_, artifactId) => {
            if (!configArtifactIds.has(artifactId)) {
                createToggleArtifact(visibilityDeps)(artifactId, false);
            }
        });
        // Load/show artifacts IN config with their opacity
        for (const config of configs) {
            toggleArtifact(config.artifactId, true);
            createUpdateArtifactOpacity(artifactState, artifactActions)(config.artifactId, config.opacity);
        }
    }, [visibilityDeps, artifactState, artifactActions, toggleArtifact]);
    const updateArtifactOpacity = useCallback((artifactId, opacity) => createUpdateArtifactOpacity(artifactState, artifactActions)(artifactId, opacity), [artifactState, artifactActions]);
    const updateArtifactWeight = useCallback((artifactId, weight) => createUpdateArtifactWeight(artifactState, artifactActions)(artifactId, weight), [artifactState, artifactActions]);
    const updateArtifactMonochrome = useCallback((artifactId, monochrome) => createUpdateArtifactMonochrome(artifactState, artifactActions)(artifactId, monochrome), [artifactState, artifactActions]);
    const isArtifactVisible = useCallback((artifactId) => createIsArtifactVisible(artifactState)(artifactId), [artifactState]);
    const isArtifactLoading = useCallback((artifactId) => createIsArtifactLoading(artifactState)(artifactId), [artifactState]);
    const validateArtifactState = useCallback((artifactId) => createValidateArtifactState({
        viewerRef,
        artifactState,
        artifactActions,
        addStatus
    })(artifactId), [artifactState, artifactActions, addStatus]);
    const showErrorInfoPanel = useCallback((title, errorMessage, errorId) => {
        const panelId = errorId || `error-${Date.now()}`;
        const infoPanelData = {
            id: panelId,
            title: title,
            content: `<div style="color: #dc2626; font-weight: 500;">${errorMessage}</div>`,
            metadata: {
                getName: () => title,
                getDescription: () => errorMessage
            }
        };
        artifactActions.addInfoPanel(infoPanelData);
        addStatus(`${title}: ${errorMessage}`);
    }, [artifactActions, addStatus]);
    const closeInfoPanel = useCallback((panelId) => {
        artifactActions.removeInfoPanel(panelId);
        artifactActions.updateVisibility(panelId, false);
        addStatus(`Closed info panel: ${panelId}`);
    }, [artifactActions, addStatus]);
    const clearAll = useCallback(() => createClearAll({
        viewerRef,
        artifactState,
        artifactActions
    })(), [artifactState, artifactActions]);
    return {
        artifactState,
        artifactActions: {
            ...artifactActions,
            clearAll
        },
        loadArtifact,
        reloadArtifact,
        toggleArtifact,
        hideAllArtifacts,
        restoreLayers,
        updateArtifactOpacity,
        updateArtifactWeight,
        updateArtifactMonochrome,
        showErrorInfoPanel,
        closeInfoPanel,
        isArtifactVisible,
        isArtifactLoading,
        validateArtifactState
    };
};
